import { Building2, Glasses, LayoutDashboard, Loader2, LogOut, Search, User as UserIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLogout, useSwitchOrg } from "@/features/auth/hooks";
import { useAuthStore } from "@/features/auth/store";
import { useAccessibleOrganisations } from "@/features/organisations/hooks";
import { cn } from "@/lib/utils";

/**
 * Écran de sélection de clinique, affiché quand aucune clinique n'est active.
 * Pensé aussi pour les profils ayant accès à TOUTES les cliniques (recherche +
 * grille de cartes). Tant qu'aucune clinique n'est choisie, les pages cliniques
 * ne sont pas rendues : évite tout affichage inter-cliniques.
 *
 * Cet écran remplace la navbar principale : il porte donc ses propres issues
 * (déconnexion, profil, console éditeur) pour ne jamais enfermer l'utilisateur
 * — en particulier l'administrateur plateforme, qui n'a pas de clinique et doit
 * pouvoir créer/configurer depuis sa console.
 */
export function ClinicSelectionScreen() {
  const me = useAuthStore((s) => s.me);
  const query = useAccessibleOrganisations();
  const switchOrg = useSwitchOrg();
  const logout = useLogout();
  const [search, setSearch] = useState("");

  const isPlatform = !!me?.is_platform_staff;
  const all = query.data?.organisations ?? [];
  const isAll = query.data?.mode === "all";
  const showSearch = all.length > 6;

  const filtered = useMemo(() => {
    const list = query.data?.organisations ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (o) => o.name.toLowerCase().includes(q) || o.slug.toLowerCase().includes(q),
    );
  }, [query.data?.organisations, search]);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Barre haute : identité + issues. L'écran de sélection n'a pas la navbar
          principale ; sans ces actions l'utilisateur serait sans échappatoire. */}
      <header className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
        <span className="flex items-center gap-2 text-sm text-foreground">
          <Glasses className="h-5 w-5 text-primary" />
          <span className="font-semibold uppercase tracking-[0.2em]">MADA</span>
        </span>
        <div className="flex items-center gap-2">
          {me?.email && (
            <span className="hidden max-w-[12rem] truncate text-xs text-muted-foreground sm:inline">
              {me.email}
            </span>
          )}
          <Button variant="outline" size="sm" asChild>
            <Link to="/profil" className="gap-1.5">
              <UserIcon className="h-4 w-4" /> Profil
            </Link>
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => logout.mutate()}
            disabled={logout.isPending}
          >
            <LogOut className="h-4 w-4" /> Se déconnecter
          </Button>
        </div>
      </header>

      <div className="flex flex-1 items-center justify-center px-4 py-10">
        <div className="w-full max-w-4xl space-y-6">
          {/* En-tête de marque */}
          <div className="flex flex-col items-center gap-2 text-center">
            <Glasses className="h-10 w-10 text-primary" />
            <span className="text-xs font-semibold uppercase tracking-[0.3em] text-muted">MADA</span>
            <h1 className="mt-2 text-2xl font-semibold text-foreground">Choisir une clinique</h1>
            <p className="max-w-md text-sm text-muted-foreground">
              {isAll
                ? "Vous avez accès à toutes les cliniques. Sélectionnez celle sur laquelle travailler."
                : "Sélectionnez la clinique sur laquelle travailler pour afficher ses données."}
            </p>
          </div>

          {/* Administrateur plateforme : la console éditeur est son point d'entrée
              (création de cliniques, licences, configuration). Évite l'impasse
              « Aucune clinique accessible ». */}
          {isPlatform && (
            <div className="mx-auto flex max-w-md flex-col items-center gap-3 border border-primary/30 bg-primary/5 p-4 text-center">
              <p className="text-sm text-foreground">
                Vous êtes administrateur plateforme. Créez et configurez les
                cliniques depuis votre console éditeur.
              </p>
              <Button asChild>
                <Link to="/editeur" className="gap-1.5">
                  <LayoutDashboard className="h-4 w-4" /> Ouvrir la console éditeur
                </Link>
              </Button>
            </div>
          )}

          {showSearch && (
            <div className="relative mx-auto max-w-md">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher une clinique (nom, code)…"
                className="pl-9"
                autoFocus
              />
            </div>
          )}

          {query.isLoading ? (
            <div className="flex justify-center py-10 text-muted">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : all.length === 0 ? (
            <p className="border border-border bg-surface py-10 text-center text-sm text-muted">
              {isPlatform
                ? "Aucune clinique pour le moment. Créez-en une depuis la console éditeur."
                : "Aucune clinique accessible."}
            </p>
          ) : filtered.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted">Aucune clinique ne correspond à « {search} ».</p>
          ) : (
            <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((o) => {
                const busy = switchOrg.isPending && switchOrg.variables === o.id;
                const disabled = switchOrg.isPending;
                return (
                  <li key={o.id}>
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => switchOrg.mutate(o.id)}
                      className={cn(
                        "group flex w-full items-center gap-3 border border-border bg-surface p-4 text-left transition-colors",
                        "hover:border-primary hover:bg-primary/5",
                        disabled && "cursor-not-allowed opacity-60 hover:border-border hover:bg-surface",
                      )}
                    >
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center bg-primary/10 text-primary">
                        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Building2 className="h-5 w-5" />}
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-foreground">{o.name}</span>
                        <span className="block truncate font-mono text-[11px] uppercase text-muted">
                          {o.slug} · {o.patient_code_prefix}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
